/**
 * The facts every session screen shows, in one place so they read the same on
 * all of them: what BC holds about this session and nothing computed here.
 */
import type { CountSession } from "../api/types";
import { formatPostingDate, formatTimestamp, isUnsetBcDate } from "../sessions/format";

export function SessionFacts({ session, mine }: { session: CountSession; mine: boolean }) {
  return (
    <dl className="facts">
      <div>
        <dt>Status</dt>
        <dd>
          <span className={`chip chip--${session.status.toLowerCase()}`}>{session.status}</span>
        </dd>
      </div>
      <div>
        <dt>Responsible manager</dt>
        <dd>
          {session.responsible_manager || "—"}
          {mine && " (you)"}
        </dd>
      </div>
      <div>
        <dt>Posting date</dt>
        <dd>{formatPostingDate(session.posting_date)}</dd>
      </div>
      <div>
        <dt>Items counted</dt>
        <dd>{session.line_count}</dd>
      </div>
      <div>
        <dt>Started</dt>
        <dd>{formatTimestamp(session.created_at)}</dd>
      </div>
      {session.submitted_at && (
        <div>
          <dt>Submitted for review</dt>
          <dd>{formatTimestamp(session.submitted_at)}</dd>
        </div>
      )}
      {session.posted_at && !isUnsetBcDate(session.posted_at) && (
        <div>
          <dt>Posted</dt>
          <dd>{formatTimestamp(session.posted_at)}</dd>
        </div>
      )}
      {session.posting_document_no && (
        <div>
          <dt>Posted as</dt>
          <dd>
            <code>{session.posting_document_no}</code>
          </dd>
        </div>
      )}
      <div>
        <dt>Session id</dt>
        <dd>
          <code className="small">{session.session_id}</code>
        </dd>
      </div>
    </dl>
  );
}

/**
 * BC's error text, verbatim and first.
 *
 * **Verbatim matters.** It names the line and the item that failed; a paraphrase
 * loses both. **First matters** because the only way back is *submit for review
 * again*, and the AL clears the text as it makes that transition
 * (`LSCountSession.Table.al` `ValidateApiTransition`) — so a screen that put the
 * resubmit button above the words would be offering to destroy them unread.
 */
export function PostFailure({ session }: { session: CountSession }) {
  if (!session.error_text) return null;
  return (
    <section className="card error" data-testid="post-failure">
      <h2>This count did not post</h2>
      <p className="verbatim" data-testid="error-text">
        {session.error_text}
      </p>
      <p className="muted small">
        Business Central's own words, unedited. The way forward is to fix what it
        names and submit the count for review again — and <strong>that clears
        this message</strong>, so read it, or copy it, before you do.
      </p>
    </section>
  );
}
